import { ArrowRightLeft, PhoneIncoming, PhoneOutgoing, Users, Zap } from "lucide-react";
import { callTypeStyle } from "@/components/ui/outcome";
import { cn } from "@/lib/utils";

/** How a call came about (ACL-508): a caller who rang in, a lead the dialer reached, or a call a
 *  setter handed on, read off the stay in the line (`QueueSession.kind`). A leg on Call history can
 *  also be a number dialed by hand or a call between colleagues: core-api says which (`Cdr.call_type`,
 *  ACL-649, platform ADR-212). */
export type CallType = "inbound" | "dialer" | "handoff" | "manual" | "internal";

export function callTypeOf(kind: string | null | undefined): CallType {
  return kind === "outbound" ? "dialer" : kind === "handoff" ? "handoff" : "inbound";
}

/** The type of a call that may never have met the ACD (a leg on the Calls page, the phone's own
 *  call): null for no kind, as a number dialed by hand, so nothing is claimed for it. */
export function knownCallType(kind: string | null | undefined): CallType | null {
  return kind === "inbound" || kind === "outbound" || kind === "handoff" ? callTypeOf(kind) : null;
}

/** The type of one of a lead's calls (`LeadCardCall`): a stay in a line by its kind, and a dial the
 *  dialer placed (`preview`, `power`) a dialer call. A dial a rep placed by hand has none. */
export function leadCallType(call: { via: string; kind: string | null }): CallType | null {
  if (call.via === "dialer") return call.kind === "preview" || call.kind === "power" ? "dialer" : null;
  return knownCallType(call.kind);
}

/** The words of a call type, for a place too small for the chip (a title, a label). */
export function callTypeWords(type: CallType): string {
  return WORDS[type];
}

const WORDS: Record<CallType, string> = {
  inbound: "Inbound call",
  dialer: "Dialer call",
  handoff: "Handed to you",
  manual: "Manual call",
  internal: "Internal call",
};

/** The chip's words in a table's column, where a row has little room: the floor's words for them. */
const SHORT: Record<CallType, string> = {
  inbound: "Inbound",
  dialer: "Dialer",
  handoff: "Handoff",
  manual: "Manual",
  internal: "Internal",
};

const ICONS = {
  inbound: PhoneIncoming, dialer: Zap, handoff: ArrowRightLeft, manual: PhoneOutgoing, internal: Users,
} satisfies Record<CallType, unknown>;

/** The call type as a chip, in its own colour and words: never told by colour alone. */
export function CallTypeChip({
  type, size = "md", short = false, className,
}: {
  type: CallType;
  /** `sm` for a row of a list or a table, beside other small marks. */
  size?: "sm" | "md";
  /** One word ("Dialer"), for a table's column; the whole words are its hover. */
  short?: boolean;
  className?: string;
}) {
  const Icon = ICONS[type];
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full border font-semibold whitespace-nowrap",
        size === "sm" ? "h-4 px-1.5 text-2xs" : "h-6 px-2 text-xs",
        type !== "inbound" && type !== "dialer" && "border-border bg-surface-sunken text-text-muted",
        className,
      )}
      style={type === "inbound" || type === "dialer" ? callTypeStyle(type) : undefined}
      title={short ? WORDS[type] : undefined}
    >
      <Icon className={size === "sm" ? "size-3" : "size-3.5"} aria-hidden />
      {short ? SHORT[type] : WORDS[type]}
    </span>
  );
}
